<template>
	<section
		class="markets-coverage-row w-full xl:mb-8 overflow-hidden"
		ref="containerEl"
	>
		<div class="flex items-center gap-4 mb-4 px-0 lg:px-0">
			<Subtitle
				class="title-font"
				:weight="alternative ? 'medium' : 'medium'"
				:class="alternative ? 'text-white' : ''"
			>
				Markets Coverage
			</Subtitle>
			<span
				class="inline-flex items-center text-xs font-bold px-2 lg:px-3 py-2 rounded-md"
				:class="
					alternative ? 'bg-white/[.1] text-white' : 'bg-[#F4F5F7] text-[#F04438]'
				"
			>
				<span class="w-2 h-2 rounded-full mr-2 bg-[#F04438] live-pulse"></span>
				LIVE
			</span>
		</div>

		<!-- Show skeleton while loading or when loaded but empty -->
		<SlideshowSkeleton
			v-if="news.length === 0"
			:item-count="5"
			:item-width="itemWidth"
			:should-show-overlay="shouldShowOverlay"
			:alternative="alternative"
			:static-mode="loaded"
		/>

		<!-- Show actual content when loaded -->
		<div
			v-else
			:class="['relative w-full']"
		>
			<!-- Timeline markers and headlines as a sliding carousel -->
			<div class="relative z-10 pt-2 overflow-x-hidden h-[124px] lg:h-[132px]">
				<span
					class="w-full h-1 bg-gradient-to-r from-[#50B5FF] to-[#BE55FF] absolute left-0 top-[4px]"
				/>
				<div
					class="flex absolute left-0 top-0 h-full transition-transform duration-500"
					:style="`width: ${
						news.length * itemWidth
					}px; transform: translateX(-${startIdx * itemWidth}px);`"
				>
					<NuxtLink
						v-for="(item, idx) in news"
						:ref="idx === news.length - 1 ? 'lastElement' : undefined"
						:key="'headline-' + idx"
						:class="[
							'flex flex-col items-start relative flex-shrink-0 ',
							alternative ? 'hover-text-white' : 'hover-text-black'
						]"
						:style="{
							'min-width': `${itemWidth}px`,
							'max-width': `${itemWidth}px`
						}"
						:to="item.link"
					>
						<div class="relative w-full h-3 flex items-center mb-2">
							<span
								class="w-full h-1 bg-gradient-to-r from-[#50B5FF] to-[#BE55FF]"
							></span>
							<span
								class="absolute top-0 bottom-0 left-0 w-[12px] h-[12px] bg-gradient-to-r from-[#50B5FF] to-[#BE55FF] rounded-full"
							></span>
						</div>

						<!-- Headline -->
						<h3
							:class="[
								'text-lg font-medium leading-tight text-left pr-4 line-clamp-3 mb-2 hover:underline',
								alternative
									? idx === startIdx
										? 'text-white'
										: 'text-white/70 hover-text-white'
									: idx === startIdx
										? 'text-[#101828]'
										: 'text-[#B2B7C2] font-bold hover-text-black'
							]"
							v-html="item.title"
						></h3>
						<!-- Time below title -->
						<div
							:class="[
								'text-sm font-medium text-left',
								alternative ? 'text-white/60' : 'text-[#B2B7C2]'
							]"
						>
							{{ daysAgo(item.date, true) }}
						</div>
					</NuxtLink>
				</div>
			</div>

			<span
				v-show="shouldShowOverlay"
				class="coverage-row-overlay"
				:class="alternative ? 'alternative' : ''"
			/>
		</div>
		<!-- Slide Controllers -->
		<div
			class="flex items-center justify-start gap-4 lg:gap-6 w-full lg:w-auto px-0 lg:px-0"
		>
			<button
				@click="prevSlide"
				:disabled="startIdx === 0"
				class="w-12 h-12 rounded-full flex items-center border justify-center transition-transform duration-150 active:scale-90"
				:class="{
					'border-white/[.5] hover:border-white bg-transparent': alternative,
					'bg-white  border-[#D1D5DB]': !alternative
				}"
			>
				<svg
					width="18"
					height="18"
					viewBox="0 0 18 18"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M11.25 13.5L6.75 9L11.25 4.5"
						:stroke="alternative ? 'rgba(255, 255, 255, 0.5)' : '#666D80'"
						stroke-width="1.125"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
			</button>
			<!-- Navigation dots - hidden on mobile -->
			<div
				class="hidden gap-2"
				:class="{ 'lg:hidden': alternative }"
			>
				<span
					v-for="idx in dotCount"
					:key="'dot-' + (idx - 1)"
					:class="[
						'w-3 h-3 rounded-full border transition-transform duration-300 cursor-pointer',
						startIdx === idx - 1
							? 'border-2 border-black bg-[#101828] scale-125'
							: 'border-slate-200 bg-white scale-100'
					]"
					@click="goToSlide(idx - 1)"
				/>
			</div>
			<button
				@click="nextSlide"
				:disabled="!canScrollNext"
				class="w-12 h-12 rounded-full flex items-center border justify-center transition-transform duration-150 active:scale-90"
				:class="{
					'border-white/[.5] hover:border-white bg-transparent': alternative,
					'bg-white  border-[#D1D5DB]': !alternative
				}"
			>
				<svg
					width="18"
					height="18"
					viewBox="0 0 18 18"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M6.75 13.5L11.25 9L6.75 4.5"
						:stroke="alternative ? 'rgba(255, 255, 255, 0.5)' : '#666D80'"
						stroke-width="1.125"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
			</button>
		</div>
	</section>
</template>
<script setup>
import { breakpointsTailwind } from "@vueuse/core";
import {
	ref,
	computed,
	onMounted,
	onBeforeUnmount,
	nextTick,
	watch
} from "vue";
import { daysAgo } from "~/utils";

const containerEl = ref(null);
const lastElement = ref(null);
const itemWidth = ref(300);
const windowSize = ref(4);

// State for intersection observer
const isLastElementVisible = ref(false);
const observer = ref(null);

const breakpoints = useBreakpoints(breakpointsTailwind);

const props = defineProps({
	news: {
		type: Array,
		required: true
	},
	alternative: {
		type: Boolean,
		default: false
	},
	loaded: {
		type: Boolean,
		default: false
	}
});

const updateDimensions = () => {
	if (containerEl.value) {
		const containerWidth = containerEl.value.clientWidth;
		if (breakpoints.isSmaller("md")) {
			windowSize.value = 1;
			itemWidth.value = containerWidth / 1.25;
		} else if (breakpoints.isSmaller("lg")) {
			windowSize.value = 2;
			itemWidth.value = containerWidth / 2;
		} else {
			windowSize.value = 3.25;
			itemWidth.value = containerWidth / 3.5;
		}
	}
};

// Setup intersection observer for last element
const setupIntersectionObserver = () => {
	if (observer.value) {
		observer.value.disconnect();
	}

	// Ensure we have valid elements before setting up observer
	if (!containerEl.value || !lastElement.value) {
		return;
	}

	const rootElement = containerEl.value.querySelector(".overflow-x-hidden");
	if (!rootElement) {
		return;
	}

	const options = {
		root: rootElement,
		threshold: 0.1,
		rootMargin: "0px"
	};

	observer.value = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.target === lastElement.value) {
				isLastElementVisible.value = entry.isIntersecting;

				if (entry.isIntersecting) {
					// When last element is visible, expand to fit all remaining items
					const remainingItems = props.news.length - startIdx.value;
					const containerWidth = containerEl.value?.clientWidth || 0;
					if (remainingItems > 0 && containerWidth > 0) {
						// Adjust item width to fit remaining items
						const newItemWidth = containerWidth / remainingItems;
						if (newItemWidth > 200) {
							// minimum reasonable width
							itemWidth.value = newItemWidth;
						}
					}
				}
			}
		});
	}, options);

	// Observe the last element
	if (lastElement.value && lastElement.value instanceof Element) {
		observer.value.observe(lastElement.value);
	}
};

onMounted(() => {
	updateDimensions();
	window.addEventListener("resize", updateDimensions);

	// Setup observer after DOM is ready
	nextTick(() => {
		if (props.news && props.news.length > 0) {
			// Wait for elements to be rendered
			setTimeout(() => {
				// Double-check that elements exist before setting up observer
				if (lastElement.value && containerEl.value) {
					setupIntersectionObserver();
				}
			}, 200);
		}
	});
});

// Watch for news changes and re-setup observer
watch(
	() => props.news,
	(newNews) => {
		if (newNews && newNews.length > 0) {
			nextTick(() => {
				setTimeout(() => {
					// Double-check that elements exist before setting up observer
					if (lastElement.value && containerEl.value) {
						setupIntersectionObserver();
					}
				}, 200);
			});
		}
	},
	{ immediate: false }
);

onBeforeUnmount(() => {
	window.removeEventListener("resize", updateDimensions);
	if (observer.value) {
		observer.value.disconnect();
	}
});

const startIdx = ref(0);
const maxStartIdx = computed(() => {
	if (isLastElementVisible.value) {
		// When last element is visible, don't allow further scrolling
		return startIdx.value;
	}
	return Math.max(0, props.news.length - windowSize.value);
});

const dotCount = computed(() => {
	const count = Math.ceil(maxStartIdx.value) + 1;
	return count > 0 ? count : 0;
});

// Navigation should be disabled when last element is visible
const canScrollNext = computed(() => {
	return !isLastElementVisible.value && startIdx.value < maxStartIdx.value + 2;
});

const shouldShowOverlay = computed(() => {
	// Hide overlay if last element is visible
	if (isLastElementVisible.value) {
		return false;
	}

	// Hide overlay if third-to-last element is highlighted (startIdx points to elements[-3])
	const thirdToLastIdx = props.news.length - 3;
	if (startIdx.value === thirdToLastIdx) {
		return false;
	}

	// Hide overlay if we're showing the last two elements
	const lastTwoElementsStartIdx = props.news.length - 2;
	if (startIdx.value >= lastTwoElementsStartIdx) {
		return false;
	}

	return true;
});

function prevSlide() {
	if (startIdx.value > 0) {
		startIdx.value--;
	}
}
function nextSlide() {
	if (canScrollNext.value) {
		startIdx.value++;
		// Reset item width when scrolling
		if (!isLastElementVisible.value) {
			updateDimensions();
		}
	}
}

function goToSlide(idx) {
	if (idx >= 0 && idx <= maxStartIdx.value) {
		startIdx.value = idx;
	}
}
</script>
<style scoped lang="scss">
@use "~/assets/utils.scss" as *;

.slide-fade-move,
.slide-fade-enter-active,
.slide-fade-leave-active {
	transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-fade-enter-from {
	/* opacity: 0; */
	transform: translateX(40px);
}
.slide-fade-leave-to {
	/* opacity: 0; */
	transform: translateX(-40px);
}

/* Mobile-specific improvements */
@media (max-width: 1023px) {
	.markets-coverage-row {
		padding-left: 0;
		padding-right: 0;
	}

	/* Ensure navigation controls are left-aligned on mobile */
	.markets-coverage-row > div:last-child {
		justify-content: flex-start;
		margin-left: 0;
	}
}
.coverage-row-overlay {
	/* Rectangle 6894 */
	top: 0;
	bottom: 0;
	position: absolute;
	width: 96px;
	height: 100%;
	right: 0px;
	z-index: 20;
	&.alternative {
		background: linear-gradient(270deg, #090d18 0%, rgba(9, 13, 24, 0) 100%);
	}
	&:not(.alternative) {
		background: linear-gradient(270deg, white 0%, rgba(255, 255, 255, 0) 100%);
	}
	@include breakpoint(lg) {
		width: 288px;
	}
	@include breakpoint(xl) {
		width: 351px;
	}
}

.hover-text-white {
	&:hover {
		color: #fff;
	}
}
.hover-text-black {
	&:hover {
		color: #101828;
	}
}

.live-pulse {
	animation: pulse-red 2s infinite;
}

@keyframes pulse-red {
	0% {
		box-shadow: 0 0 0 0 rgba(240, 68, 56, 0.7);
		transform: scale(1);
	}
	50% {
		box-shadow: 0 0 0 6px rgba(240, 68, 56, 0);
		transform: scale(1.1);
	}
	100% {
		box-shadow: 0 0 0 0 rgba(240, 68, 56, 0);
		transform: scale(1);
	}
}
</style>
